import {useRef, useState} from "react";
import styled from "styled-components";
import theme from "../../style/theme";
import ExpandLessIcon from "../icons/ExpandLessIcon";
import ExpandMoreIcon from "../icons/ExpandMoreIcon";
import {useClickOutside} from "../../utils/click/handle-click";

interface Props {
    placeholder?: string;
    onChangeTextField?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onClickSelectItem: (key: string) => void;
    resetTextField?: () => void;
    value?: string;
    candidateMap: { [key: string]: string };
    width?: string;
    isError?: boolean;
    isActive?: boolean;
    disabled?: boolean;
}

export const TimeAutocomplete: React.FC<Props> = (
    {
        placeholder,
        value,
        onChangeTextField,
        onClickSelectItem,
        resetTextField,
        candidateMap,
        width,
        isError,
        isActive,
        disabled,
    }
) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const textFieldRef = useRef<HTMLInputElement>(null);
    const [isFocus, setIsFocus] = useState(false);

    const handleClickBox = () => {
        if (disabled) {
            return;
        }
        setOpen(true);
        textFieldRef.current?.focus();
    };

    useClickOutside(ref, () => {
        setOpen(false);
        setIsFocus(false);
    });

    const onFocusTextField = () => {
        setIsFocus(true);
    };

    return (
        <div ref={ref} style={{position: "relative"}}>
            <SelectedItem width={width} isError={isError} isActive={isFocus}>
                <TextField
                    ref={textFieldRef}
                    placeholder={placeholder}
                    width={width}
                    value={value}
                    onChange={onChangeTextField}
                    disabled={disabled}
                    maxLength={2}
                    isActive={isFocus}
                    onFocus={onFocusTextField}
                />
                <RightSelectedItemWrapper onClick={handleClickBox}>
                    <Divider/>
                    <SelectedIconWrapper>
                        {open ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                    </SelectedIconWrapper>
                </RightSelectedItemWrapper>
            </SelectedItem>
            {open && Object.keys(candidateMap).length > 0 && (
                <MenuItemList width={width}>
                    {Object.keys(candidateMap).map((candidate) => {
                        const isSelected = value === candidate;
                        return (
                            <MenuItem
                                key={candidate}
                                selected={isSelected}
                                onClick={() => {
                                    onClickSelectItem(candidate);
                                    setOpen(false);
                                }}
                            >
                                <MenuItemLabel>{candidateMap[candidate]}</MenuItemLabel>
                            </MenuItem>
                        );
                    })}
                </MenuItemList>
            )}
        </div>
    );
};

interface SelectedItemProps {
    width?: string;
    isError?: boolean;
    isActive?: boolean;
}

const SelectedItem = styled.div<SelectedItemProps>`
  width: ${(props) => props.width || "14.25rem"};
  height: 42px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border: 1px solid ${(props) => {
    if (props.isActive) {
      return theme.palette.primitives.gray600;
    }
    if (props.isError) {
      return theme.palette.primitives.red500;
    }
    return theme.palette.primitives.gray200;
  }};
  background-color: ${theme.palette.primitives.white};

  &:hover {
    background-color: #f5f6f9;
  }
`;

interface TextFieldProps {
    isActive?: boolean;
}

const TextField = styled.input<TextFieldProps>`
  width: 50%;
  height: 18px;
  display: flex;
  padding: 12px;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  border: none;
  background-color: transparent;

  &:focus {
    outline: none;
  }

  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 18px;
  color: ${theme.palette.primitives.gray900};

  &::placeholder {
    font-weight: 500;
    font-size: 15px;
    line-height: 18px;
    color: ${theme.palette.primitives.gray300};
  }
`;

interface MenuItemListProps {
    width?: string;
}

const MenuItemList = styled.div<MenuItemListProps>`
  width: ${(props) => props.width};
  max-height: 12.188rem;
  padding: 6.5px 0;
  position: absolute;
  background-color: #ffffff;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0px 6px 8px rgba(0, 0, 0, 0.2);
  border: solid 1px #bec3cb;
  z-index: 500;
  // max-height: calc(100vh - 400px);
  overflow-y: scroll;
  overflow-x: hidden;

  /* 스크롤 바 스타일 */

  &::-webkit-scrollbar {
    width: 5px;
    background-color: #ffffff;
  }

  /* 스크롤바 색상 */

  &::-webkit-scrollbar-thumb {
    background-color: #1c2026;
    border-radius: 10px;
  }

  /* 스크롤바가 마우스 오버될 때 색상 */

  &::-webkit-scrollbar-thumb:hover {
    background-color: #bfbfbf;
  }
`;

interface MenuItemProps {
    selected: boolean;
}

const MenuItem = styled.div<MenuItemProps>`
  padding-left: 12px;
  height: 36px;
  display: flex;
  color: #3f444e;
  align-items: center;
  background-color: ${(props) => (props.selected ? "#FFF7D9" : "#ffffff")};

  &:hover {
    background-color: #f5f6f9;
  }

  font-size: 14px;
  font-weight: 500;
`;

const MenuItemLabel = styled.div`
  height: 2.5rem;
  font-style: normal;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: ${theme.palette.primitives.black};
  display: flex;
  align-items: center;
`;

const RightSelectedItemWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Divider = styled.div`
  width: 1px;
  height: 26px;
  background: ${theme.palette.primitives.gray200};
`;

const SelectedIconWrapper = styled.div`
  display: flex;
  padding: 0 13px;
  justify-content: center;
  align-items: center;
`;
